import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { CourseMaterial, CourseMaterialCategoryLabel } from '../../../../core/interfaces/course-material';
import { StudentCourseMaterialService } from '../../../../core/services/student-course-material.service';
import { ExternalNavigationService } from '../../../../core/services/utils/external-navigation.service';
import { CourseMaterialCardComponent } from '../../../ui/views/course-material-card/course-material-card.component';
import { MaterialPreviewComponent } from '../../../ui/views/material-preview/material-preview.component';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';

interface CategoryFilter {
  key: 'ALL' | CourseMaterialCategoryLabel;
  i18nKey: string;
  icon: string;
}

const CATEGORY_FILTERS: CategoryFilter[] = [
  { key: 'ALL',      i18nKey: 'LIBRARY.FILTER_ALL',         icon: 'fa-solid fa-grip' },
  { key: 'Pdf',      i18nKey: 'LIBRARY.CATEGORY.Pdf',       icon: 'fa-solid fa-file-pdf' },
  { key: 'Video',    i18nKey: 'LIBRARY.CATEGORY.Video',     icon: 'fa-solid fa-circle-play' },
  { key: 'Image',    i18nKey: 'LIBRARY.CATEGORY.Image',     icon: 'fa-solid fa-image' },
  { key: 'Slide',    i18nKey: 'LIBRARY.CATEGORY.Slide',     icon: 'fa-solid fa-file-powerpoint' },
  { key: 'Document', i18nKey: 'LIBRARY.CATEGORY.Document',  icon: 'fa-solid fa-file-lines' },
  { key: 'Audio',    i18nKey: 'LIBRARY.CATEGORY.Audio',     icon: 'fa-solid fa-headphones' },
  { key: 'Other',    i18nKey: 'LIBRARY.CATEGORY.Other',     icon: 'fa-solid fa-paperclip' },
];

@Component({
  selector: 'app-digital-lib',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CourseMaterialCardComponent,
    MaterialPreviewComponent,
    ErrorStateComponent,
  ],
  templateUrl: './digital-lib.component.html',
  styleUrl: './digital-lib.component.css',
})
export class DigitalLibComponent implements OnInit {
  private readonly service = inject(StudentCourseMaterialService);
  private readonly navigation = inject(ExternalNavigationService);

  readonly categoryFilters = CATEGORY_FILTERS;

  // ─── Reactive state via signals ────────────────────────────
  readonly materials = signal<CourseMaterial[]>([]);
  readonly loading = signal<boolean>(false);
  readonly errored = signal<boolean>(false);

  /** Support ouvert dans la modale d'aperçu. */
  readonly previewTarget = signal<CourseMaterial | null>(null);

  /**
   * Fonction de téléchargement passée à l'aperçu. Liée ici parce que le chemin
   * dépend du rôle : l'élève passe par `students/me/course-materials`.
   */
  readonly downloadFn = (id: number) => this.service.download(id);

  readonly selectedCategory = signal<CategoryFilter['key']>('ALL');
  readonly selectedSubject = signal<string>('ALL');
  readonly query = signal<string>('');
  readonly showFavoritesOnly = signal<boolean>(false);

  /** Distinct subject names available in the loaded set, used to feed the dropdown. */
  readonly availableSubjects = computed(() => {
    const set = new Set<string>();
    for (const m of this.materials()) {
      if (m.subjectName) set.add(m.subjectName);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  /** Filtered list applied to the grid (search + category + subject). */
  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    const cat = this.selectedCategory();
    const sub = this.selectedSubject();
    const favOnly = this.showFavoritesOnly();
    return this.materials().filter((m) => {
      if (favOnly && !m.isFavorite) return false;
      if (cat !== 'ALL' && m.category !== cat) return false;
      if (sub !== 'ALL' && m.subjectName !== sub) return false;
      if (!q) return true;
      const haystack = [
        m.title,
        m.description,
        m.subjectName,
        m.themeTitle,
        m.professorFullName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  });

  ngOnInit(): void {
    this.loadMaterials();
  }

  setCategory(key: CategoryFilter['key']): void {
    this.selectedCategory.set(key);
  }

  setSubject(name: string): void {
    this.selectedSubject.set(name);
  }

  onQueryInput(value: string): void {
    this.query.set(value);
  }

  toggleFavorites(): void {
    this.showFavoritesOnly.update(v => !v);
  }

  resetFilters(): void {
    this.selectedCategory.set('ALL');
    this.selectedSubject.set('ALL');
    this.query.set('');
    this.showFavoritesOnly.set(false);
  }

  /** Trigger a download via Blob + anchor click. */
  onDownload(material: CourseMaterial): void {
    this.service.download(material.id).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) return;
        const filename = this.extractFilename(response.headers.get('Content-Disposition'))
          ?? material.fileName
          ?? `${material.title || 'document'}${material.extension ?? ''}`;
        this.saveBlob(blob, filename);
      },
      error: (err) => {
        console.error('Download failed:', err);
      },
    });
  }

  /** Open the external link in a new tab. */
  onOpenExternal(material: CourseMaterial): void {
    this.navigation.open(material.externalUrl);
  }

  /** Toggle favorite status for a material. */
  onFavoriteToggle(material: CourseMaterial, isFavorite: boolean): void {
    const obs = isFavorite
      ? this.service.addFavorite(material.id)
      : this.service.removeFavorite(material.id);

    // Optimistic update
    this.materials.update(list =>
      list.map(m => m.id === material.id ? { ...m, isFavorite } : m)
    );

    obs.subscribe({
      error: () => {
        // Revert on failure
        this.materials.update(list =>
          list.map(m => m.id === material.id ? { ...m, isFavorite: !isFavorite } : m)
        );
      },
    });
  }

  /** Rechargement manuel, déclenché par <app-error-state>. */
  reload(): void {
    this.loadMaterials();
  }

  // ───────────────────────────────────────────────────────────
  private loadMaterials(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.service.getMyMaterials().subscribe({
      next: (res) => {
        this.materials.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.materials.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Read filename from `Content-Disposition: attachment; filename="..."` */
  private extractFilename(header: string | null): string | null {
    if (!header) return null;
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';\r\n]+)["']?/i.exec(header);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
